import { ReactNode, RefObject } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineXL, Text14 } from '@sorare/core/src/atoms/typography';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { navLabels } from '@sorare/core/src/lib/glossary';

import { Block } from '../Block';
import { ProfileCta } from '../ProfileCta';
import collectionsBadge from './assets/collections-badge.svg';

const StyledBlock = styled(Block)`
  min-height: 330px;
  scroll-padding-bottom: 200px;
`;
const Row = styled.div`
  display: flex;
  gap: var(--unit);
  overflow-x: auto;
`;
const Wrapper = styled.div`
  --collection-bottom-background: var(--c-neutral-300);
  --collection-size: 250px;
  min-width: 250px;
  width: 250px;
`;
const EmptyRoot = styled(Vertical).attrs({ center: true })`
  flex: 1;
  padding: var(--double-unit);
  text-align: center;
`;

export type Props<T extends { slug: string }> = {
  backgroundImage?: string;
  totalCollections: number;
  collectionsUrl: string;
  collections: T[];
  renderCollection: (collection: T) => ReactNode;
  wrapperRef?: RefObject<HTMLDivElement>;
};

export const Collections = <T extends { slug: string }>({
  backgroundImage,
  totalCollections,
  collectionsUrl,
  collections,
  renderCollection,
  wrapperRef,
}: Props<T>) => {
  const { slug } = useParams();
  const { isMe } = useCurrentUserContext();

  if (!isMe(slug) && !totalCollections) {
    return null;
  }

  return (
    <StyledBlock
      ref={wrapperRef}
      title={<FormattedMessage {...navLabels.collections} />}
      icon={<img src={collectionsBadge} alt="collections" />}
      stat={{
        count: totalCollections,
        label: (
          <FormattedMessage
            id="ManagerProfile.Collections.started"
            defaultMessage="Started"
          />
        ),
      }}
      cta={
        <ProfileCta to={collectionsUrl}>
          {!totalCollections ? (
            <FormattedMessage
              id="ManagerProfile.Collections.startCollecting"
              defaultMessage="Start collecting"
            />
          ) : (
            <FormattedMessage
              id="ManagerProfile.Collections.seeCollections"
              defaultMessage="See collections"
            />
          )}
        </ProfileCta>
      }
      backgroundImage={!totalCollections ? backgroundImage : undefined}
    >
      {totalCollections ? (
        <Row>
          {collections.slice(0, 10).map(collection => (
            <Wrapper key={collection.slug}>
              {renderCollection(collection)}
            </Wrapper>
          ))}
        </Row>
      ) : (
        <EmptyRoot>
          <HeadlineXL as="h3">
            <FormattedMessage
              id="ManagerProfile.Collections.completeCollections"
              defaultMessage="Complete collections"
            />
          </HeadlineXL>
          <Text14 color="var(--c-neutral-500)">
            <FormattedMessage
              id="ManagerProfile.Collections.scoutYourFavoriteTeam"
              defaultMessage="Scout your favorite team’s players"
            />
          </Text14>
        </EmptyRoot>
      )}
    </StyledBlock>
  );
};
